import globalMaterialUITheme from '@alo/yoga-ui';
import DateFnsUtils from '@date-io/date-fns';
import { TextField, ThemeProvider } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import React, { useContext, useMemo } from 'react';
import RadioButtons from 'src/scripts/templates/_shared/ui-components/YogaUI/RadioButtons';
import { ProductContext } from '../../../ProductContext';
import FormInput from 'src/scripts/templates/_shared/ui-components/YogaUI/FormInput';

export type GiftCardRecipientType = 'someoneElse' | 'me';
export type GiftCardSendDateType = 'now' | 'later';

export interface GiftCardRecipientFields {
  name: string;
  email: string;
  message: string;
  delivery_date?: Date;
}

export const noWhitespaceRegex = '^(?!\\s*$).+';
export const emailRegex = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';

const GiftCardRecipientUI = () => {
  const {
    giftCardRecipientType,
    giftCardSendDateType,
    setGiftCardRecipientType,
    setGiftCardSendDateType,
    giftCardRecipientFields,
    updateGiftCardRecipientFields,
  } = useContext(ProductContext);

  const characterCount = useMemo(() => {
    return giftCardRecipientFields?.message.length;
  }, [giftCardRecipientFields]);

  const today = new Date();
  const giftCardSendDateThreshold = 90;
  const minSendDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const maxSendDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + giftCardSendDateThreshold);

  // the below two values are from: https://shopify.dev/docs/storefronts/themes/product-merchandising/gift-cards
  const GIFT_CARD_RECIPIENT_NAME_MAX_LENGTH = 255;
  const GIFT_MESSAGE_MAX_LENGTH = 200;

  const handleRecipientChange = (value: string) => {
    const recipientType = value as GiftCardRecipientType;
    setGiftCardRecipientType(recipientType);
  };

  const handleSendDateTypeChange = (value: string) => {
    const sendDateType = value as GiftCardSendDateType;
    setGiftCardSendDateType(sendDateType);
    const deliveryDate = sendDateType === 'later' ? minSendDate : undefined;
    updateGiftCardRecipientFields({ delivery_date: deliveryDate });
  };

  const handleSendDateChange = (date: Date | null) => {
    if (date) {
      updateGiftCardRecipientFields({ delivery_date: date });
    }
  };

  const handleNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    const name = event.currentTarget.value;
    updateGiftCardRecipientFields({ name: name });
  };

  const handleEmailChange = (event: React.FormEvent<HTMLInputElement>) => {
    const email = event.currentTarget.value;
    updateGiftCardRecipientFields({ email: email });
  };

  const renderSendDateSelectionUI = () => {
    switch (giftCardSendDateType) {
      case 'now':
        return null;
      case 'later':
        return (
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              margin="normal"
              variant="inline"
              id="date-picker-dialog"
              className="delivery-date-picker"
              label="Please Select Delivery Date"
              format="MM/dd/yyyy"
              value={giftCardRecipientFields.delivery_date}
              onChange={handleSendDateChange}
              keyboardIcon={<span className="alo-schedule-icon"></span>}
              InputProps={{ readOnly: true }}
              PopoverProps={{
                disablePortal: true,
              }}
              disablePast={true}
              allowKeyboardControl={true}
              autoOk
              minDate={minSendDate}
              maxDate={maxSendDate}
              helperText="Gift cards are sent approximately between 4:00 am–10:00 am PST"
            />
          </MuiPickersUtilsProvider>
        );
    }
  };

  const renderRecipientUI = () => {
    switch (giftCardRecipientType) {
      case 'me':
        return <p>Once purchased, you will receive an email with the card and instructions how to redeem it.</p>;
      case 'someoneElse':
        return (
          <form className="gift-card-recipient-selection__form-group form-group d-flex">
            <div className="d-flex flex-column gift-card-recipient-selection__input-containers">
              <FormInput
                options={{
                  id: 'RecipientName',
                  label: 'Recipient Name',
                  type: 'text',
                  onChange: handleNameChange,
                  errorText: 'Please enter the name of the recipient.',
                  name: 'recipient_name',
                  required: true,
                  maxLength: GIFT_CARD_RECIPIENT_NAME_MAX_LENGTH,
                  pattern: noWhitespaceRegex,
                  defaultValue: giftCardRecipientFields.name,
                }}
              />
              <FormInput
                options={{
                  id: 'RecipientEmail',
                  label: 'Recipient Email Address',
                  type: 'email',
                  onChange: handleEmailChange,
                  errorText: 'Please enter a valid email address.',
                  name: 'recipient_email',
                  required: true,
                  pattern: emailRegex,
                  defaultValue: giftCardRecipientFields.email,
                }}
              />
            </div>
            <div className="d-flex flex-column gift-card-recipient-selection__input-containers">
              <h5 className="h5 semibold gift-card-recipient-selection__header-text">Add a message (optional)</h5>
              <p className="hint-text">Hint: Let the recipient know who the gift card is from in your message.</p>
              <TextField
                id="GiftCardMessage"
                label="Gift Message"
                value={giftCardRecipientFields.message}
                onChange={(e) => {
                  updateGiftCardRecipientFields({ message: e.currentTarget.value });
                }}
                name="gift_card_message"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                inputProps={{
                  maxLength: GIFT_MESSAGE_MAX_LENGTH,
                }}
                helperText={`${characterCount}/${GIFT_MESSAGE_MAX_LENGTH}`}
              />
            </div>
            <div className="d-flex flex-column gift-card-recipient-selection__input-containers">
              <h5 className="h5 semibold gift-card-recipient-selection__header-text">Select Delivery Date</h5>
              <RadioButtons
                options={{
                  labelValuePairs: [
                    ['Send now', 'now'],
                    ['Send at a later time', 'later'],
                  ],
                  radioGroupClasses: 'gift-card-recipient-selection__radio-buttons',
                  radioRowClasses: 'gift-card-recipient-selection__radio-button',
                  defaultValue: giftCardSendDateType,
                }}
                setRadioValue={handleSendDateTypeChange}
              />
              <>{renderSendDateSelectionUI()}</>
            </div>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={globalMaterialUITheme}>
      <div id="GiftCardRecipientSelection" className="gift-card-recipient-selection d-flex flex-column">
        <h5 className="h5 semibold gift-card-recipient-selection__header-text">Who is it for?</h5>
        <RadioButtons
          options={{
            labelValuePairs: [
              ['For someone else', 'someoneElse'],
              ['For me', 'me'],
            ],
            radioGroupClasses: 'gift-card-recipient-selection__radio-buttons',
            radioRowClasses: 'gift-card-recipient-selection__radio-button',
            defaultValue: giftCardRecipientType,
          }}
          setRadioValue={handleRecipientChange}
        />
        <>{renderRecipientUI()}</>
      </div>
    </ThemeProvider>
  );
};

export default GiftCardRecipientUI;
